/**
 * Utility functions for ScannerConfigurationForm
 */
import {
  AUTH_METHOD_FIELDS,
  AUTH_FIELD_DEFAULTS,
  BASE_CONNECTION_FIELDS,
  DAY_NAME_MAP,
  DEFAULT_FORM_VALUES,
  SENSITIVE_FIELDS_BY_AUTH_METHOD
} from './constants';

/**
 * Helper function to resolve a value from multiple possible paths in the data object.
 * This abstracts the repetitive property lookup logic (DRY principle).
 *
 * @param {Object} data - The source data object
 * @param {string} field - The field name to look for
 * @param {*} defaultValue - The default value if not found
 * @param {string[]} paths - Nested paths to check (e.g., ['parameters', 'config'])
 * @returns {*} The resolved value or default
 */
export const resolveField = (
  data,
  field,
  defaultValue = '',
  paths = ['parameters', 'config']
) => {
  if (!data) return defaultValue;

  // Check direct field first
  // Preserve null and undefined values explicitly (used for sensitive fields)
  // Use 'in' operator to distinguish between undefined value vs missing property
  if (field in data) {
    return data[field];
  }

  // Check nested paths in order of priority
  for (const path of paths) {
    const nestedData = data[path];

    // Only use 'in' operator on objects (not strings, arrays, etc.)
    if (nestedData && typeof nestedData === 'object' && field in nestedData) {
      return nestedData[field];
    }
  }

  return defaultValue;
};

/**
 * Get scenario-specific field defaults based on target system and auth method.
 * Returns the empty field structure for a specific authentication scenario.
 *
 * @param {string} targetSystem - The target system identifier
 * @param {string} authMethod - The authentication method
 * @returns {Object} Field defaults (empty strings) for the scenario
 */
export const getScenarioFieldDefaults = (targetSystem, authMethod) => {
  // Amazon Bedrock with API Key
  if (targetSystem === 'amazon-bedrock' && authMethod === 'access-key') {
    return {
      accessKeyId: '',
      secretAccessKey: '',
      awsRegion: ''
    };
  }

  // Microsoft Azure Copilot with OAuth2
  if (targetSystem === 'azure-openai' && authMethod === 'oauth') {
    return {
      tenantId: '',
      clientId: '',
      clientSecret: '',
      scope: ''
    };
  }

  // Google Vertex AI with Service Account
  if (targetSystem === 'vertex-ai' && authMethod === 'service-account') {
    return {
      serviceAccountEmail: '',
      projectId: '',
      privateKey: '',
      location: ''
    };
  }

  // Generic fallback based on auth method
  return AUTH_FIELD_DEFAULTS[authMethod] || {};
};

/**
 * Helper function to get auth-specific fields based on the target system and authentication method.
 * Only initializes fields relevant to the selected scenario.
 *
 * @param {Object} initialData - The source data object
 * @param {string} targetSystem - The target system identifier
 * @param {string} authMethod - The authentication method
 * @returns {Object} Auth-specific fields with their values
 */
export const getAuthSpecificFields = (
  initialData,
  targetSystem,
  authMethod
) => {
  const fieldDefaults = getScenarioFieldDefaults(targetSystem, authMethod);

  if (!fieldDefaults || Object.keys(fieldDefaults).length === 0) {
    return {};
  }

  return Object.entries(fieldDefaults).reduce((acc, [field, defaultValue]) => {
    acc[field] = resolveField(initialData, field, defaultValue);

    return acc;
  }, {});
};

/**
 * Helper function to get all connection-related fields
 * @param {string} authMethod - The authentication method
 * @returns {string[]} Array of connection field names
 */
export const getConnectionFields = (authMethod) => {
  const authSpecificFields = AUTH_METHOD_FIELDS[authMethod] || [];

  return [...BASE_CONNECTION_FIELDS, ...authSpecificFields];
};

/**
 * Helper function to check if a field is connection-related
 * @param {string} field - The field name to check
 * @param {string} authMethod - The authentication method
 * @returns {boolean} True if the field is connection-related
 */
export const isConnectionField = (field, authMethod) => {
  const connectionFields = getConnectionFields(authMethod);

  return connectionFields.includes(field);
};

/**
 * Helper function to get current day of week
 * @returns {string} Current day name (e.g., 'Monday')
 */
export const getCurrentDayOfWeek = () => {
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];
  const today = new Date();

  return days[today.getDay()];
};

/**
 * Get current day of week as integer (0-6, where 0 is Sunday)
 * @returns {number} Current day of week as integer
 */
export const getCurrentDayOfWeekInt = () => {
  const today = new Date();

  return today.getDay();
};

/**
 * Convert day name string to integer (0-6, where 0 is Sunday)
 * @param {string} dayName - Day name (e.g., 'Monday', 'Sunday')
 * @returns {number} Day of week as integer (0-6)
 */
export const dayNameToInt = (dayName) => {
  const dayMap = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6
  };

  return dayMap[dayName] !== undefined ? dayMap[dayName] : 1; // Default to Monday
};

/**
 * Convert integer (0-6) to day name string
 * @param {number} dayInt - Day of week as integer (0-6, where 0 is Sunday)
 * @returns {string} Day name (e.g., 'Monday', 'Sunday')
 */
export const intToDayName = (dayInt) => {
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];

  return days[dayInt] || 'Monday'; // Default to Monday
};

/**
 * Get ordinal suffix for a day number
 * @param {number} day - Day number (1-31)
 * @returns {string} Ordinal suffix ('st', 'nd', 'rd', 'th')
 */
export const getOrdinalSuffix = (day) => {
  const lastDigit = day % 10;
  const lastTwoDigits = day % 100;

  // Special case for 11, 12, 13 (they use 'th')
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return 'th';
  }

  // Check last digit for other numbers
  if (lastDigit === 1) return 'st';
  if (lastDigit === 2) return 'nd';
  if (lastDigit === 3) return 'rd';

  return 'th';
};

/**
 * Helper function to get current day of month with ordinal suffix
 * @returns {string} Current day with suffix (e.g., '4th')
 */
export const getCurrentDayOfMonth = () => {
  const today = new Date();
  const day = today.getDate();

  return `${day}${getOrdinalSuffix(day)}`;
};

/**
 * Parse schedule from JSON string or object
 * @param {string|Object} scheduleString - Schedule data
 * @returns {Object|null} Parsed schedule object or null
 */
const parseScheduleData = (scheduleString) => {
  if (!scheduleString) return null;

  try {
    return typeof scheduleString === 'string'
      ? JSON.parse(scheduleString)
      : scheduleString;
  } catch (e) {
    return null;
  }
};

/**
 * Initialize runFrequency field from various data sources
 * @param {Object} initialData - Initial data object
 * @returns {Object|string} Run frequency value
 */
const initializeRunFrequency = (initialData) => {
  // Try direct runFrequency field first (backward compatibility)
  const directRunFrequency = resolveField(initialData, 'runFrequency', null, [
    'runPolicy',
    'advancedSettings'
  ]);

  if (directRunFrequency) {
    return directRunFrequency;
  }

  // Try to parse schedule JSON string
  const scheduleData = parseScheduleData(initialData?.schedule);

  // Extract schedule components
  const unit =
    scheduleData?.unit ||
    resolveField(initialData, 'unit', null, ['runPolicy', 'advancedSettings']);
  const interval =
    scheduleData?.interval ||
    resolveField(initialData, 'interval', null, [
      'runPolicy',
      'advancedSettings'
    ]);
  const on =
    scheduleData?.on !== undefined
      ? scheduleData.on
      : resolveField(initialData, 'on', null, [
          'runPolicy',
          'advancedSettings'
        ]);

  // Build runFrequency object from flattened structure
  if (unit) {
    const parsedInterval =
      typeof interval === 'string' ? parseInt(interval, 10) : interval || 1;

    const runFreqObj = {
      unit,
      interval: parsedInterval
    };

    if (on !== null && on !== undefined) {
      runFreqObj.on = typeof on === 'string' ? parseInt(on, 10) : on;
    }

    return runFreqObj;
  }

  // Default fallback
  return DEFAULT_FORM_VALUES.runFrequency;
};

/**
 * Initialize 'at' field (run time) from various data sources
 * @param {Object} initialData - Initial data object
 * @returns {string} Run time value (always a string for Select component)
 */
export const initializeAtField = (initialData) => {
  const scheduleData = parseScheduleData(initialData?.schedule);

  if (scheduleData) {
    // Check 'at' first (new format), then 'runTime' (backward compatibility)
    if (scheduleData.at !== undefined) {
      // Convert to string if it's a number (API may return number, form expects string)
      return String(scheduleData.at);
    }
    if (scheduleData.runTime !== undefined) {
      // Convert to string if it's a number
      return String(scheduleData.runTime);
    }
  }

  // Try to resolve from other paths and convert to string if needed
  const resolvedAt = resolveField(initialData, 'at', DEFAULT_FORM_VALUES.at, [
    'runPolicy',
    'advancedSettings'
  ]);

  // Ensure it's a string (form expects string values like "0", "1", "2", etc.)
  return String(resolvedAt);
};

/**
 * Initialize syncReview field from various data sources
 * @param {Object} initialData - Initial data object
 * @returns {string} Sync review value
 */
const initializeSyncReview = (initialData) => {
  const scheduleData = parseScheduleData(initialData?.schedule);

  if (scheduleData?.syncReview) {
    return scheduleData.syncReview;
  }

  return resolveField(
    initialData,
    'syncReview',
    DEFAULT_FORM_VALUES.syncReview,
    ['runPolicy', 'advancedSettings']
  );
};

/**
 * Clear sensitive fields in edit mode for security
 * @param {Object} fields - Form fields object
 * @param {string} authMethod - Authentication method
 * @returns {Object} Fields with sensitive data cleared
 */
const clearSensitiveFieldsInEditMode = (fields, authMethod) => {
  const sensitiveFields = SENSITIVE_FIELDS_BY_AUTH_METHOD[authMethod] || [];
  const clearedFields = { ...fields };

  sensitiveFields.forEach((field) => {
    if (clearedFields[field]) {
      clearedFields[field] = '';
    }
  });

  return clearedFields;
};

/**
 * Helper function to initialize form data from props.
 * Only initializes fields relevant to the specific authentication method,
 * avoiding "ghost data" and keeping the state clean.
 *
 * @param {Object} initialData - The initial data object from props
 * @returns {Object} Clean form data with only relevant fields
 */
export const initializeFormData = (initialData, mode = 'add') => {
  // Determine the auth method first (check aliased fields: method vs authMethod)
  const authMethod =
    resolveField(initialData, 'method', '') ||
    resolveField(initialData, 'authMethod', '', []);

  // Common fields that are always needed regardless of auth method
  const targetSystem =
    resolveField(initialData, 'target', '') ||
    resolveField(initialData, 'targetSystem', '', []);

  const commonFields = {
    id: initialData?.id || undefined,
    connectionId: initialData?.connection?.id || undefined,
    name: initialData?.name || '',
    description: initialData?.description || '',
    targetSystem,
    authMethod,
    // Run policy fields
    runFrequency: initializeRunFrequency(initialData),
    at: initializeAtField(initialData),
    syncReview: initializeSyncReview(initialData),
    // Advanced settings
    timeout: resolveField(initialData, 'timeout', undefined, [
      'schedule',
      'runPolicy',
      'advancedSettings'
    ]),
    maxRetryAttempts: resolveField(initialData, 'maxRetryAttempts', undefined, [
      'schedule',
      'runPolicy',
      'advancedSettings'
    ]),
    emailNotificationsEnabled:
      resolveField(initialData, 'emailNotificationsEnabled', null, [
        'advancedSettings'
      ]) ??
      resolveField(initialData, 'notificationEnabled', null, []) ??
      DEFAULT_FORM_VALUES.emailNotificationsEnabled,
    recipientEmails: resolveField(
      initialData,
      'recipientEmails',
      DEFAULT_FORM_VALUES.recipientEmails,
      ['advancedSettings']
    )
  };

  // Get only the auth-specific fields relevant to the selected scenario
  const specificFields = getAuthSpecificFields(
    initialData,
    targetSystem,
    authMethod
  );

  // Merge common fields with auth-specific fields
  let mergedFields = {
    ...commonFields,
    ...specificFields
  };

  // In edit mode, clear sensitive fields for security
  if (mode === 'edit' && authMethod) {
    mergedFields = clearSensitiveFieldsInEditMode(mergedFields, authMethod);
  }

  return mergedFields;
};

/**
 * Generate a schedule label from custom schedule settings
 * @param {Object} customSchedule - Custom schedule configuration
 * @returns {string} Human-readable schedule label
 */
export const generateScheduleLabel = (customSchedule) => {
  const { intervalNumber, intervalUnit, repeatDays, monthDay } = customSchedule;

  if (intervalUnit === 'weeks') {
    const selectedDay = repeatDays[0] || 'Monday';
    const dayName = DAY_NAME_MAP[selectedDay] || selectedDay;

    if (intervalNumber === 1) {
      return `Weekly on ${dayName}`;
    }

    return `Every ${intervalNumber} weeks on ${dayName}`;
  }

  if (intervalUnit === 'months') {
    return `Monthly on the ${monthDay}${getOrdinalSuffix(monthDay)}`;
  }

  if (intervalNumber === 1) {
    return 'Daily';
  }

  return `Every ${intervalNumber} days`;
};

/**
 * Generate month day options for custom schedule
 * @returns {Array<{label: string, value: string}>} Month day options
 */
export const generateMonthDayOptions = () =>
  Array.from({ length: 30 }, (_, i) => {
    const day = i + 1;

    return {
      label: `${day}${getOrdinalSuffix(day)}`,
      value: `${day}`
    };
  });

/**
 * Generate interval number options based on interval unit
 * @param {string} intervalUnit - The interval unit ('days', 'weeks', 'months')
 * @returns {Array<{label: string, value: string}>} Interval number options
 */
export const generateIntervalNumberOptions = (intervalUnit) => {
  if (intervalUnit === 'months') {
    return [{ label: '1', value: '1' }];
  }

  // Limit weeks to 1-3 options
  if (intervalUnit === 'weeks') {
    return [
      { label: '1', value: '1' },
      { label: '2', value: '2' },
      { label: '3', value: '3' }
    ];
  }

  // Days can have 1-6 options
  return [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' }
  ];
};

/**
 * Generate a label from structured schedule object
 * @param {Object} scheduleObj - Structured schedule object
 * @returns {string} Human-readable schedule label
 */
export const generateLabelFromStructuredSchedule = (scheduleObj) => {
  if (!scheduleObj || typeof scheduleObj !== 'object') {
    return '';
  }

  const { unit, interval, on } = scheduleObj;

  if (unit === 'week') {
    // on is now an integer (0-6), convert to day name for display
    const dayName = typeof on === 'number' ? intToDayName(on) : on || 'Monday';

    if (interval === 1) {
      return `Weekly on ${dayName}`;
    }

    return `Every ${interval} weeks on ${dayName}`;
  }

  if (unit === 'month') {
    const day = on || 1;

    return `Monthly on the ${day}${getOrdinalSuffix(day)}`;
  }

  // day
  if (interval === 1) {
    return 'Daily';
  }

  return `Every ${interval} days`;
};

/**
 * Convert custom schedule to structured object format
 * @param {Object} customSchedule - Custom schedule configuration
 * @returns {Object} Structured schedule object
 */
export const convertCustomScheduleToObject = (customSchedule) => {
  const { intervalNumber, intervalUnit, repeatDays, monthDay } = customSchedule;

  if (intervalUnit === 'weeks') {
    const selectedDay = repeatDays[0] || 'M';
    const dayName = DAY_NAME_MAP[selectedDay] || selectedDay;
    // Convert day name to integer (0-6)
    const dayInt = dayNameToInt(dayName);

    return {
      unit: 'week',
      interval: intervalNumber,
      on: dayInt
    };
  }

  if (intervalUnit === 'months') {
    return {
      unit: 'month',
      interval: intervalNumber,
      on: monthDay
    };
  }

  // days
  return {
    unit: 'day',
    interval: intervalNumber
  };
};

/**
 * Compare two schedule objects for equality
 * @param {Object} obj1 - First schedule object
 * @param {Object} obj2 - Second schedule object
 * @returns {boolean} True if objects are equal
 */
export const compareScheduleObjects = (obj1, obj2) => {
  if (obj1 === obj2) {
    return true;
  }

  if (
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object' ||
    obj1 === null ||
    obj2 === null
  ) {
    return obj1 === obj2;
  }

  // Normalize objects by sorting keys for consistent comparison
  const normalize = (obj) => {
    const sorted = {};

    Object.keys(obj)
      .sort()
      .forEach((key) => {
        sorted[key] = obj[key];
      });

    return sorted;
  };

  return JSON.stringify(normalize(obj1)) === JSON.stringify(normalize(obj2));
};

/**
 * Generate a unique string identifier for a schedule object
 * @param {Object} scheduleObj - Schedule object
 * @returns {string} Unique identifier string
 */
export const generateScheduleId = (scheduleObj) => {
  if (!scheduleObj || typeof scheduleObj !== 'object') {
    return scheduleObj || '';
  }
  // Create a stable string identifier from the object properties
  const { unit, interval, on } = scheduleObj;

  if (unit === 'week') {
    return `week_${interval}_${on}`;
  }
  if (unit === 'month') {
    return `month_${interval}_${on}`;
  }

  // day
  return `day_${interval}`;
};

/**
 * Generate run frequency options based on current form state
 * @param {string|Object} runFrequency - Current run frequency value (string or object)
 * @returns {Array<{label: string, value: string, scheduleObj: Object}>} Run frequency options
 */
export const generateRunFrequencyOptions = (runFrequency) => {
  const currentDayOfWeek = getCurrentDayOfWeek(); // For display label
  const currentDayOfWeekInt = getCurrentDayOfWeekInt(); // For on property (0-6)
  const currentDayOfMonth = new Date().getDate();

  // Create base option objects
  const dailyValue = { unit: 'day', interval: 1 };
  const weeklyValue = { unit: 'week', interval: 1, on: currentDayOfWeekInt };
  const monthlyValue = { unit: 'month', interval: 1, on: currentDayOfMonth };

  // If runFrequency matches a base option, reuse that exact object reference
  // This ensures the Select component can match the value correctly
  const baseOptionValues = [dailyValue, weeklyValue, monthlyValue];

  if (typeof runFrequency === 'object' && runFrequency !== null) {
    // Check which base option runFrequency matches
    if (compareScheduleObjects(dailyValue, runFrequency)) {
      baseOptionValues[0] = runFrequency; // Use exact reference
    } else if (compareScheduleObjects(weeklyValue, runFrequency)) {
      baseOptionValues[1] = runFrequency; // Use exact reference
    } else if (compareScheduleObjects(monthlyValue, runFrequency)) {
      baseOptionValues[2] = runFrequency; // Use exact reference
    }
  }

  const baseOptions = [
    {
      label: 'Daily',
      value: generateScheduleId(baseOptionValues[0]),
      scheduleObj: baseOptionValues[0]
    },
    {
      label: `Weekly on ${currentDayOfWeek}`,
      value: generateScheduleId(baseOptionValues[1]),
      scheduleObj: baseOptionValues[1]
    },
    {
      label: `Monthly on the ${getCurrentDayOfMonth()}`,
      value: generateScheduleId(baseOptionValues[2]),
      scheduleObj: baseOptionValues[2]
    }
  ];

  const isCustomValue =
    runFrequency !== 'Custom' &&
    runFrequency !== null &&
    runFrequency !== undefined &&
    !baseOptions.some((opt) =>
      compareScheduleObjects(opt.scheduleObj, runFrequency)
    );

  if (isCustomValue && runFrequency) {
    // If runFrequency is already an object (structured format), use it directly
    if (typeof runFrequency === 'object') {
      baseOptions.push({
        label: generateLabelFromStructuredSchedule(runFrequency),
        value: generateScheduleId(runFrequency),
        scheduleObj: runFrequency
      });
    } else {
      // Backward compatibility: if it's a string, keep it as is
      baseOptions.push({
        label: runFrequency,
        value: runFrequency,
        scheduleObj: runFrequency
      });
    }
  }

  baseOptions.push({ label: 'Custom', value: 'Custom', scheduleObj: 'Custom' });

  return baseOptions;
};

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {boolean} True if valid email format
 */
export const validateEmail = (email) => {
  if (!email || email.length > 254) {
    return false;
  }
  const emailRegex = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,63}$/;

  return emailRegex.test(email);
};

/**
 * Build parameters object for form submission
 * @param {Object} formData - Form data object
 * @param {string} mode - Form mode ('add' or 'edit')
 * @param {Set<string>} modifiedSensitiveFields - Set of sensitive fields that were modified (edit mode only)
 * @returns {Object} Parameters object for API request
 *
 * Note: This is a utility function primarily used for testing.
 * In production, use buildAuthParameters from useScannerForm hook instead.
 */
export const buildSubmitParameters = (
  formData,
  mode = 'add',
  modifiedSensitiveFields = new Set()
) => {
  const sensitiveFields =
    SENSITIVE_FIELDS_BY_AUTH_METHOD[formData.authMethod] || [];

  // Helper to get field value with sensitive field handling
  const getFieldValue = (fieldName, fieldValue) => {
    const isSensitive = sensitiveFields.includes(fieldName);

    if (
      mode === 'edit' &&
      isSensitive &&
      !modifiedSensitiveFields.has(fieldName)
    ) {
      return null; // Preserve existing value in edit mode
    }

    return fieldValue;
  };

  let parameters = {
    target: formData.targetSystem,
    method: formData.authMethod
  };

  if (formData.authMethod === 'access-key') {
    parameters = {
      ...parameters,
      accessKeyId: formData.accessKeyId,
      secretAccessKey: getFieldValue(
        'secretAccessKey',
        formData.secretAccessKey
      ),
      awsRegion: formData.awsRegion
    };
  } else if (formData.authMethod === 'oauth') {
    parameters = {
      ...parameters,
      clientId: formData.clientId,
      clientSecret: getFieldValue('clientSecret', formData.clientSecret),
      authorizationServerURL: formData.authorizationServerURL,
      tokenEndpointURL: formData.tokenEndpointURL,
      redirectURI: formData.redirectURI,
      scope: formData.scope
    };
  }

  return parameters;
};

/**
 * Create a field validator function for the current context
 * @param {string[]} sensitiveFields - List of sensitive field names
 * @param {boolean} isEditMode - Whether in edit mode
 * @returns {Function} Field validator function
 */
const createFieldValidator = (sensitiveFields, isEditMode) => {
  return (fieldName, fieldValue) => {
    const isSensitive = sensitiveFields.includes(fieldName);

    // Determine if field is empty
    // null and undefined are considered empty
    // For strings, trim and check if empty
    // For other values (numbers, booleans), they are NOT empty
    let isEmpty;

    if (fieldValue === null || fieldValue === undefined) {
      isEmpty = true;
    } else if (typeof fieldValue === 'string') {
      isEmpty = fieldValue.trim() === '';
    } else {
      // Numbers (including 0), booleans (including false), etc. are not empty
      isEmpty = false;
    }

    // In edit mode, sensitive fields can be empty (means keep existing secret)
    if (isEditMode && isSensitive && isEmpty) {
      return true;
    }

    // All other cases: field must not be empty
    return !isEmpty;
  };
};

/**
 * Validate Amazon Bedrock with Access Key authentication
 */
const validateAmazonBedrockAccessKey = (formData, isFieldValid) => {
  return (
    isFieldValid('accessKeyId', formData.accessKeyId) &&
    isFieldValid('secretAccessKey', formData.secretAccessKey) &&
    isFieldValid('awsRegion', formData.awsRegion)
  );
};

/**
 * Validate Azure OpenAI with OAuth authentication
 */
const validateAzureOpenAIOAuth = (formData, isFieldValid) => {
  return (
    isFieldValid('tenantId', formData.tenantId) &&
    isFieldValid('clientId', formData.clientId) &&
    isFieldValid('clientSecret', formData.clientSecret) &&
    isFieldValid('scope', formData.scope)
  );
};

/**
 * Validate Google Vertex AI with Service Account authentication
 */
const validateVertexAIServiceAccount = (formData, isFieldValid) => {
  return (
    isFieldValid('serviceAccountEmail', formData.serviceAccountEmail) &&
    isFieldValid('projectId', formData.projectId) &&
    isFieldValid('privateKey', formData.privateKey) &&
    isFieldValid('location', formData.location)
  );
};

/**
 * Validate generic OAuth authentication
 */
const validateGenericOAuth = (formData, isFieldValid) => {
  return (
    isFieldValid('clientId', formData.clientId) &&
    isFieldValid('clientSecret', formData.clientSecret)
  );
};

/**
 * Validate auth-specific fields based on target system and auth method
 */
const validateAuthFields = (formData, isFieldValid) => {
  const { targetSystem, authMethod } = formData;

  // Amazon Bedrock with API Key
  if (targetSystem === 'amazon-bedrock' && authMethod === 'access-key') {
    return validateAmazonBedrockAccessKey(formData, isFieldValid);
  }

  // Microsoft Azure Copilot with OAuth2
  if (targetSystem === 'azure-openai' && authMethod === 'oauth') {
    return validateAzureOpenAIOAuth(formData, isFieldValid);
  }

  // Google Vertex AI with Service Account
  if (targetSystem === 'vertex-ai' && authMethod === 'service-account') {
    return validateVertexAIServiceAccount(formData, isFieldValid);
  }

  // Generic OAuth (fallback for other systems)
  if (authMethod === 'oauth') {
    return validateGenericOAuth(formData, isFieldValid);
  }

  return true;
};

/**
 * Validates form data for scanner configuration
 * In edit mode, sensitive fields (secrets) are optional since empty means "keep existing"
 *
 * @param {Object} formData - Form data to validate
 * @param {string} mode - Form mode ('add' or 'edit')
 * @returns {boolean} True if form is valid
 */
export const validateForm = (formData, mode = 'add') => {
  // Validate common required fields
  const hasName = formData.name?.trim() !== '';
  const hasTargetSystem = formData.targetSystem !== '';
  const hasAuthMethod = formData.authMethod !== '';

  if (!hasName || !hasTargetSystem || !hasAuthMethod) {
    return false;
  }

  // Validate email notifications: if enabled, must have at least one recipient email
  if (formData.emailNotificationsEnabled) {
    const hasRecipientEmails =
      formData.recipientEmails && formData.recipientEmails.length > 0;

    if (!hasRecipientEmails) {
      return false;
    }
  }

  // Create field validator for this context
  const sensitiveFields =
    SENSITIVE_FIELDS_BY_AUTH_METHOD[formData.authMethod] || [];
  const isFieldValid = createFieldValidator(sensitiveFields, mode === 'edit');

  // Validate auth-specific fields
  const authFieldsValid = validateAuthFields(formData, isFieldValid);

  return authFieldsValid;
};
